import { createRouter, createWebHistory } from "vue-router"
import { authGuard } from "@auth0/auth0-vue"
import Overview from "@/views/Overview.vue"
import { manageGuard, signupGuard, eligibilityGuard } from "@/router"

const routes = () => ([
  {
    path: "/:pathMatch(.*)*",
    redirect: "/overview",
  },
  {
    path: "/",
    name: "Overview",
    alias: "/overview",
    component: Overview,
    beforeEnter: authGuard,
  },
  {
    path: "/signup",
    name: "SignUp",
    beforeEnter: signupGuard,
    component: {
      template: "<div/>",
    },
  },
  {
    path: "/new",
    name: "New",
    component: () => import("@/views/New.vue"),
    beforeEnter: authGuard,
  },
  {
    path: "/start-trial",
    name: "StartTrial",
    component: () => import("@/views/StartTrial.vue"),
    beforeEnter: [authGuard, eligibilityGuard],
  },
  {
    path: "/account",
    name: "account",
    component: () => import("@/views/Account.vue"),
    beforeEnter: authGuard,
  },
  {
    path: "/manage/:id",
    name: "Manage",
    component: () => import("@/views/Manage.vue"),
    props: ({ params, query }) => ({
      id: params.id,
      scrollToSubscription: Boolean(query.scrollToSubscription),
    }),
    beforeEnter: [authGuard, manageGuard],
  },
  {
    path: "/invoices",
    name: "Invoices",
    component: () => import("@/views/Invoice.vue"),
    beforeEnter: authGuard,
  },
  {
    path: "/support",
    name: "Support",
    beforeEnter: () => {
      // once upon a time the support-related functionality was part of this frontend but later
      // moved. This makes sure that old links are still working by redirecting to the new frontend.
      window.location.replace(`https://support.my-prtg.com/support${window.location.search}`)
      return false
    },
  },
  {
    path: "/upload",
    name: "UploadSelect",
    component: () => import("@/views/SubscriptionSelectSkeleton.vue"),
    props: () => ({
      icon: "$mdiFileUpload",
      header: "file upload",
    }),
    beforeEnter: authGuard,
    children: [
      {
        path: ":id",
        name: "Upload",
        props: ({ params }) => ({ id: params.id }),
        component: () => import("@/views/InstanceProgressSkeleton.vue"),
        beforeEnter: manageGuard,
        children: [
          {
            path: "",
            name: "upload-default",
            component: () => import("@/views/Upload.vue"),
            props: ({ params }) => ({ id: params.id }),
          },
        ],
      },
    ],
  },
  {
    path: "/microsoft-entra-id",
    name: "MicrosoftEntraIdSelect",
    component: () => import("@/views/SubscriptionSelectSkeleton.vue"),
    props: () => ({
      icon: "$mdiMicrosoftWindows",
      header: "microsoft entra ID",
    }),
    beforeEnter: authGuard,
    children: [
      {
        path: ":id",
        name: "MicrosoftEntraId",
        props: ({ params }) => ({ id: params.id }),
        component: () => import("@/views/InstanceProgressSkeleton.vue"),
        beforeEnter: manageGuard,
        children: [
          {
            path: "",
            name: "microsoft-entra-id-default",
            component: () => import("@/views/MicrosoftEntraId.vue"),
            props: ({ params }) => ({ id: params.id }),
          },
        ],
      },
    ],
  },
  {
    path: "/multi-platform-probe",
    name: "MultiPlatformProbeSelect",
    component: () => import("@/views/SubscriptionSelectSkeleton.vue"),
    props: () => ({
      icon: "$mdiRouterNetwork",
      header: "multi-platform probe",
    }),
    beforeEnter: authGuard,
    children: [
      {
        path: ":id",
        name: "MultiPlatformProbe",
        props: ({ params }) => ({ id: params.id }),
        component: () => import("@/views/InstanceProgressSkeleton.vue"),
        beforeEnter: manageGuard,
        children: [
          {
            path: "",
            name: "multi-platform-probe-default",
            component: () => import("@/views/MultiPlatformProbe.vue"),
            props: ({ params }) => ({ id: params.id }),
          },
        ],
      },
    ],
  },
  {
    path: "/data-sharing-opt-in",
    name: "DataCollectionOptIn",
    component: () => import("@/views/DataCollectionOptIn.vue"),
    beforeEnter: authGuard,
  },
  {
    path: "/prtg-users",
    name: "PrtgUsersSelect",
    component: () => import("@/views/SubscriptionSelectSkeleton.vue"),
    props: () => ({
      icon: "$mdiAccountGroup",
      header: "PRTG users",
    }),
    beforeEnter: [authGuard, manageGuard],
    children: [
      {
        path: ":id",
        name: "PrtgUsers",
        props: ({ params }) => ({ id: params.id }),
        component: () => import("@/views/PrtgUsers.vue"),
      },
    ],
  },
])

const scrollBehavior = () => new Promise((resolve) => {
  setTimeout(() => {
    resolve({ top: 0 })
  }, 250)
})

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: routes(),
  scrollBehavior,
})

export default router
export {
  routes,
  scrollBehavior,
}
export * from "./guards.js"
